import { Text } from '@/components/text';
import { ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { Platform, StyleSheet, View } from 'react-native';

export type DropdownMenuItem<T = string> = {
  id: T;
  label: string;
  subtitle?: string;
  checked?: boolean;
  disabled?: boolean;
};

type DropdownAlign = 'start' | 'end';

type DropdownMenuProps<T = string> = {
  trigger: (onToggle: () => void) => ReactNode;
  title?: string;
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  minWidth?: number;
  maxWidth?: number;
  align?: DropdownAlign;
  children?: ReactNode;
};

export function DropdownMenu<T = string>({
  trigger,
  title,
  open,
  defaultOpen = false,
  onOpenChange,
  minWidth,
  maxWidth = 500,
  align = 'start',
  children,
}: DropdownMenuProps<T>) {
  const isControlled = open !== undefined;
  const [internalOpen, setInternalOpen] = useState(defaultOpen);
  const isOpen = isControlled ? !!open : internalOpen;

  const setOpen = (value: boolean) => {
    if (!isControlled) {
      setInternalOpen(value);
    }
    onOpenChange?.(value);
  };

  const alignStyle = useMemo(() => {
    if (align === 'start') return styles.alignStart;
    if (align === 'end') return styles.alignEnd;
    return styles.alignStart;
  }, [align]);

  const menuRef = useRef<View>(null);
  const buttonRef = useRef<View>(null);

  // Cerrar el menú al hacer clic fuera (solo en web)
  useEffect(() => {
    if (!isOpen || Platform.OS !== 'web') return;

    const handleClickOutside = (event: any) => {
      const target = event.target;

      // Get the DOM nodes from the refs
      const menuNode = menuRef.current as any;
      const buttonNode = buttonRef.current as any;

      if (menuNode && buttonNode) {
        // Check if the click is outside both the menu and button
        const clickedOutsideMenu = !menuNode.contains?.(target);
        const clickedOutsideButton = !buttonNode.contains?.(target);

        if (clickedOutsideMenu && clickedOutsideButton) {
          setOpen(false);
        }
      }
    };

    // Delay para evitar que el clic del botón cierre inmediatamente el menú
    const timeoutId = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside);
    }, 100);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  return (
    <View style={styles.container}>
      <View style={styles.buttonContainer}>
        <View ref={buttonRef} {...(Platform.OS === 'web' ? ({ 'data-button-instance': 'true' } as any) : {})}>
          {trigger(() => setOpen(!isOpen))}
        </View>
        {isOpen && (
          <View
            ref={menuRef}
            style={[styles.dropdownMenu, alignStyle, { minWidth: minWidth || 'auto', maxWidth }]}
            {...(Platform.OS === 'web' ? ({ 'data-menu-instance': 'true' } as any) : {})}
            onStartShouldSetResponder={() => true}
            onResponderTerminationRequest={() => false}>
            {title && (
              <View style={styles.menuHeader}>
                <Text type='defaultSemiBold'>{title}</Text>
              </View>
            )}
            {children}
          </View>
        )}
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    position: 'relative',
    zIndex: 2,
  },
  buttonContainer: {
    position: 'relative',
    alignSelf: 'flex-start',
    zIndex: 10,
  },

  dropdownMenu: {
    position: 'absolute',
    top: '100%',
    marginTop: 8,
    backgroundColor: 'white',
    borderRadius: 12,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.2,
    shadowRadius: 12,
    elevation: 8,
    borderWidth: 1,
    borderColor: 'rgba(0,0,0,0.08)',
    overflow: 'hidden',
    zIndex: 1000,
  },

  alignStart: {
    left: 0,
  },
  alignEnd: {
    right: 0,
  },
  menuHeader: {
    padding: 16,
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(0,0,0,0.08)',
    backgroundColor: 'rgba(0,0,0,0.02)',
  },
});
